/**
 * What a valid trigger is, in one place.
 *
 * The values are shaped for the person, except for `firesOn`, which keeps the
 * wire's shape: a trigger source is an event subscription or a schedule, never
 * both, so a reader has to narrow on its `type` before it can touch either
 * side. Each rule sits on the smallest thing it is about, which is what puts
 * every error beside its own control: a field's rule on the field, a source's
 * on that source, the shared invocation block's inside it. The root holds the
 * condition's rules, which read the source, and the checks that a target's
 * required agent variables and action parameters are filled. Because those
 * stay out of `targetSchema`, a card missing one still parses in
 * `buildTriggerRequest`'s previews.
 */

import { z } from "zod";

import {
  filePatternsSchema,
  invocationSpecSchema,
  missingRequiredParameters,
  required,
} from "@/shared/components/invocationSpec/invocationSpecSchema";
import { actionNameError } from "@/shared/domain/actions";
import { type AnalysisScope } from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  entityOf,
  legalOncePerFor,
  OncePer,
  PlatformEventType,
  scheduleToUtcCron,
  ThreadVisibility,
  TriggerSourceType,
  TriggerTargetType,
  variableRoots,
} from "@/shared/domain/triggers";

import { conditionRootViolations } from "../Trigger/triggerConditionFilters";

import {
  completeCondition,
  isCompleteRow,
  type ConditionValues,
} from "./conditionValues";
import { cronError } from "./cronError";
import { TRIGGER_FORM_ENTITIES } from "./formScope";

/**
 * The form's side of the SDK's `EventSubscription`. `entity` is form-only: the
 * wire infers it from the events, but the Select needs a value before any
 * change is checked.
 */
const eventSubscriptionSchema = z
  .object({
    type: z.literal(TriggerSourceType.Events),
    entity: z.enum(TRIGGER_FORM_ENTITIES),
    events: z
      .array(z.enum(PlatformEventType))
      .min(1, "Check at least one change."),
    oncePer: z.enum(OncePer),
  })
  .superRefine((subscription, ctx) => {
    const { entity, events, oncePer } = subscription;
    if (events.some((event) => entityOf(event) !== entity)) {
      ctx.addIssue({
        code: "custom",
        path: ["events"],
        message: "Every change must belong to the chosen entity.",
      });
    }
    if (events.length > 0 && !legalOncePerFor(events).includes(oncePer)) {
      ctx.addIssue({
        code: "custom",
        path: ["oncePer"],
        message: "Choose a granularity every checked change supports.",
      });
    }
  });

const scheduleSchema = z
  .object({
    type: z.literal(TriggerSourceType.Schedule),
    preset: z.enum(["hourly", "daily", "weekly", "custom"]),
    /** Clock time "HH:MM" in `timeZone`. */
    time: z.string(),
    /** ISO weekday, 1 = Monday … 7 = Sunday; `weekly` only. */
    weekday: z.number().int().min(1).max(7),
    timeZone: z.string(),
    /** The raw expression; `custom` only, and always UTC. */
    cron: z.string(),
  })
  .superRefine((schedule, ctx) => {
    if (schedule.preset === "custom") {
      const error = cronError(schedule.cron);
      if (error !== undefined) {
        ctx.addIssue({ code: "custom", path: ["cron"], message: error });
      }
    } else if (scheduleToUtcCron(schedule) === null) {
      ctx.addIssue({
        code: "custom",
        path: ["time"],
        message: "Enter a valid time.",
      });
    }
  });

const firesOnSchema = z.discriminatedUnion("type", [
  eventSubscriptionSchema,
  scheduleSchema,
]);

const invokeActionTargetSchema = z.object({
  type: z.literal(TriggerTargetType.InvokeAction),
  targetId: z.string(),
  spec: invocationSpecSchema,
  /**
   * The wire's `required_inputs`, which a file firing is gated by: the changed
   * file fires the trigger only if it matches one of them. A dataset firing
   * mounts through the invocation block's inputs instead, and sends none.
   */
  requiredInputs: filePatternsSchema,
  /**
   * The chosen action's required parameters, noted by the card once the
   * action loads. An invocation missing one is refused,
   * so a trigger saved without it would fail every time it fired.
   */
  requiredParameters: z.array(z.string()),
  /**
   * `additional_inputs` a stored trigger already has, carried through exactly
   * as stored. The form creates none, shows them read-only, and can remove
   * them, so saving does not erase inputs it has no control for.
   */
  storedAdditionalInputs: z.array(z.string()),
});

const startAgentTargetSchema = z.object({
  type: z.literal(TriggerTargetType.StartAgent),
  targetId: z.string(),
  agentId: required(z.string(), "Select an agent to start."),
  /** Agent variable name to a template string resolved against the event namespace. */
  variables: z.record(z.string(), z.string()),
  /**
   * The chosen agent's variables that are required with no default, noted
   * when the agent is picked so validation can insist on them. Not sent.
   */
  requiredVariables: z.array(z.string()),
  visibility: z.enum(ThreadVisibility),
  analysisScope: z.custom<AnalysisScope>().nullable(),
});

const sendSlackMessageTargetSchema = z.object({
  type: z.literal(TriggerTargetType.SendSlackMessage),
  targetId: z.string(),
  channelId: required(z.string(), "Select a Slack channel."),
  text: z.string().trim().min(1, "Enter a message to send."),
});

export const targetSchema = z.discriminatedUnion("type", [
  invokeActionTargetSchema,
  startAgentTargetSchema,
  sendSlackMessageTargetSchema,
]);

/** The longest trigger name: the SDK's `MAX_TRIGGER_NAME_LENGTH`. */
export const TRIGGER_NAME_MAX_LENGTH = 256;

const newName = z
  .string()
  .min(1, "Enter a trigger name.")
  .max(TRIGGER_NAME_MAX_LENGTH, `Up to ${TRIGGER_NAME_MAX_LENGTH} characters.`)
  .refine(
    (name) => !actionNameError(name),
    "Names cannot contain spaces or special characters.",
  );

export interface TriggerFormSchemaOptions {
  /** The name is asked for, and checked, only when the trigger is created. */
  mode: "create" | "edit";
}

/**
 * Validation for the whole trigger form.
 *
 * A factory only because of the name: on edit it is the stored identifier,
 * shown read-only, and carried through unjudged, since the UI's name pattern
 * is stricter than the server's and would block every save of a trigger made
 * through the SDK.
 */
export function makeTriggerFormSchema({ mode }: TriggerFormSchemaOptions) {
  return z
    .object({
      name: mode === "create" ? newName : z.string(),
      /** "Enable on create"; the update request's `enabled` on edit. */
      enabled: z.boolean(),
      firesOn: firesOnSchema,
      condition: z.custom<ConditionValues>(),
      targets: z.array(targetSchema).min(1, "Add at least one target."),
    })
    .superRefine(({ firesOn, condition, targets }, ctx) => {
      // A schedule has nothing to condition on, so no condition card is judged.
      if (firesOn.type === TriggerSourceType.Events) {
        if (condition.type === "rows") {
          condition.rows.forEach((row, index) => {
            if (!isCompleteRow(row)) {
              ctx.addIssue({
                code: "custom",
                path: ["condition", "rows", index, "values"],
                message: "Enter a value to compare against.",
              });
            }
          });
        }
        const violations = conditionRootViolations(
          completeCondition(condition),
          variableRoots(firesOn.events),
        );
        if (violations.length > 0) {
          ctx.addIssue({
            code: "custom",
            path: ["condition"],
            message: `These conditions reference properties not shared by every selected change: ${violations.join(", ")}. Remove them or change the selection.`,
          });
        }
        // Without a condition, the invocations the action target starts fire
        // the trigger again when they finish.
        if (
          firesOn.entity === "invocation" &&
          targets.some((t) => t.type === TriggerTargetType.InvokeAction) &&
          completeCondition(condition) === null
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["condition"],
            message:
              "Add a condition, such as on the action's name. Without one, the invocations this trigger starts fire it again when they finish.",
          });
        }
      }
      targets.forEach((target, index) => {
        if (target.type === TriggerTargetType.StartAgent) {
          for (const name of target.requiredVariables) {
            if ((target.variables[name] ?? "").trim() === "") {
              ctx.addIssue({
                code: "custom",
                path: ["targets", index, "variables", name],
                message:
                  "The agent needs this. Pick a trigger variable or enter a value.",
              });
            }
          }
        }
        if (target.type === TriggerTargetType.InvokeAction) {
          for (const name of missingRequiredParameters(
            target.spec.parameters,
            target.requiredParameters,
          )) {
            ctx.addIssue({
              code: "custom",
              path: ["targets", index, "spec", "parameters", name],
              message:
                "The action needs this. Pick a trigger variable or enter a value.",
            });
          }
        }
      });
    });
}

export type TriggerFormValues = z.input<
  ReturnType<typeof makeTriggerFormSchema>
>;
/** What the resolver hands `handleSubmit`: every picker is already non-null. */
export type TriggerFormOutput = z.output<
  ReturnType<typeof makeTriggerFormSchema>
>;
export type TargetFormValues = z.input<typeof targetSchema>;
export type TargetFormOutput = z.output<typeof targetSchema>;
export type InvokeActionTargetValues = z.input<typeof invokeActionTargetSchema>;
export type StartAgentTargetValues = z.input<typeof startAgentTargetSchema>;
export type SendSlackMessageTargetValues = z.input<
  typeof sendSlackMessageTargetSchema
>;
/** The form's side of the SDK's `EventSubscription`, inferred from the union. */
export type EventSubscriptionValues = Extract<
  TriggerFormValues["firesOn"],
  { type: TriggerSourceType.Events }
>;
export type ScheduleValues = Extract<
  TriggerFormValues["firesOn"],
  { type: TriggerSourceType.Schedule }
>;
